import {ReactNode} from "react";
import {Box} from "@chakra-ui/react";
import {Tooltip} from "../../../../../components/ui/tooltip";

/**
 * Wraps a locked-out control (or region) so hovering it explains why it's unavailable. When `locked`
 * is false the children render untouched. The default message is entity-neutral (it's shared by the
 * population / sample / field surfaces); pass `content` to override it.
 *
 * Two modes:
 * - default (single control): the wrapped control should set `disabled` + `pointerEvents="none"` so
 *   hover falls through to this wrapper and the tooltip can open.
 * - `fill` (a whole region of inputs, e.g. a sampling-methodology design block): renders full-width
 *   and itself dims + blocks interaction on the children, so the callers don't have to disable each
 *   control individually.
 */
export const LockHint = ({
  locked,
  content,
  children,
  fill,
}: {
  locked: boolean;
  content?: ReactNode;
  children: ReactNode;
  fill?: boolean;
}) => {
  if (!locked) return <>{children}</>;
  const tip = content ?? "Locked — unlock to make changes.";
  return (
    <Tooltip content={tip}>
      <Box as="span" display={fill ? "flex" : "inline-flex"} w={fill ? "full" : undefined} cursor="not-allowed">
        {fill ? (
          <Box w="full" pointerEvents="none" opacity={0.6}>
            {children}
          </Box>
        ) : (
          children
        )}
      </Box>
    </Tooltip>
  );
};
